// Localised content for The Living Book.
// CHAPTERS now reads from i18n so chapter titles + prompts switch language live.
import i18n from "../i18n";

const _chapterRomans = ["I", "II", "III", "IV", "V", "VI", "VII"];

function buildChapter(i) {
  const t = i18n.t.bind(i18n);
  const prompts = [
    t(`chapters.ch${i}P1`),
    t(`chapters.ch${i}P2`),
    t(`chapters.ch${i}P3`),
  ];
  // Optional 4th prompt (currently used by ch3 — Family recipes, and
  // ch7 — Tips and tricks). i18next returns the key verbatim when a
  // translation is missing, so compare against the key to detect absence.
  const p4Key = `chapters.ch${i}P4`;
  const p4 = t(p4Key);
  if (p4 && p4 !== p4Key) prompts.push(p4);
  return {
    id: i,
    roman: _chapterRomans[i - 1],
    title: t(`chapters.ch${i}Title`),
    subtitle: t(`chapters.ch${i}Sub`),
    prompts,
  };
}

// Function for components that want to refresh on language change:
export function getChapters() {
  return [1, 2, 3, 4, 5, 6, 7].map(buildChapter);
}

// Proxy that always reflects current language at access time.
// Components iterating CHAPTERS.map(...) will get translated values.
export const CHAPTERS = new Proxy([], {
  get(_target, prop) {
    const arr = getChapters();
    if (prop === "length") return arr.length;
    if (prop === Symbol.iterator) return arr[Symbol.iterator].bind(arr);
    // numeric index
    const i = typeof prop === "string" ? Number(prop) : NaN;
    if (!Number.isNaN(i)) return arr[i];
    // delegate to array methods (.map, .filter, .find, ...)
    const v = arr[prop];
    return typeof v === "function" ? v.bind(arr) : v;
  },
});

export const FAMILY_QUOTES = [
  { quote: "The conversation is the gift. The book is simply where it comes to rest.", author: "The Living Book" },
  { quote: "Every family carries a library of stories worth keeping.", author: "The Living Book" },
  { quote: "We don't just preserve stories. We begin conversations.", author: "The Living Book" },
  { quote: "Some books are written. Yours is lived.", author: "The Living Book" },
];

// Relation keys (translated in components via t(`relations.${key}`))
export const RELATIONS = [
  "Parent", "Grandparent", "Great-grandparent", "Child", "Grandchild",
  "Aunt", "Uncle", "Sibling", "Partner", "Other",
];

// Base book prices (Phase 3 — Founding pricing tiered by SKU)
export const EDITIONS = [
  { id: "softcover", name: "Classic Softcover",
    size: "A5 · Perfect bound · ~50 pages · Premium paper",
    price: 49.99,
    blurb: "A beautifully bound paperback. A first chapter of your family's living book." },
  { id: "hardcover", name: "Heritage Hardcover",
    size: "A4 · Hardcover · Premium cream paper · ~80 pages",
    price: 74.99,
    badge: "Most chosen",
    blurb: "Substantial. Library-quality. The book you'll see on the shelf for decades." },
  { id: "heirloom", name: "Heirloom Edition",
    size: "A4 · Premium hardcover · Superior paper stock · ~100+ pages",
    price: 119.99,
    blurb: "Our finest binding. A book built to be held, kept, and passed down." },
];

export const SKU_BOOK_DISCOUNT = {
  individual_founding: 0.20,
  family_founding:     0.25,
  individual:          0.15,
  family:              0.20,
};

export const SKU_LABEL = {
  individual_founding: "Founding Individual · 20% off",
  family_founding:     "Founding Family · 25% off",
  individual:          "Subscriber · 15% off",
  family:              "Family · 20% off",
};

export function bookPriceForUser(edition, me) {
  const sku = me?.subscription_sku;
  const tier = me?.subscription_tier;
  if (!sku || tier === "free" || !SKU_BOOK_DISCOUNT[sku]) {
    return { price: edition.price, base: edition.price, discount: 0, label: null };
  }
  const discount = SKU_BOOK_DISCOUNT[sku];
  return {
    price: Number((edition.price * (1 - discount)).toFixed(2)),
    base: edition.price,
    discount,
    label: SKU_LABEL[sku],
  };
}

export const SUBSCRIPTION_TIERS = {
  individual: {
    sku: "individual",
    name: "The Living Book",
    headline: "Unlock your whole book.",
    founding: { price: 49, badge: "Founding Member" },
    standard: { price: 79, badge: null },
    features: [
      "One primary storyteller",
      "Invite up to four family contributors or readers",
      "All seven chapters and 21 questions",
      "Voice recording and transcription",
      "Thoughtful AI follow-up questions",
      "Photographs beside memories",
      "Full Living Book preview",
    ],
    coming_soon: [
      "Sunday morning prompts via email",
      "PDF export",
      "Personalised printed books",
    ],
  },
  family: {
    sku: "family",
    name: "The Living Book Family",
    headline: "Built for the whole family.",
    founding: { price: 89, badge: "Founding Member · Best value" },
    standard: { price: 149, badge: "Best value" },
    features: [
      "Up to five storytellers",
      "Shared family space",
      "Family questions and collaborative stories",
      "Visual family tree",
      "Remembrance profiles",
      "Everything included in Individual",
    ],
    coming_soon: [
      "Annual edition concierge",
      "Personalised printed books",
    ],
  },
};

export const B2B_TIERS = [
  { id: "care_home",        name: "Care Homes",          price_blurb: "From £500/yr per home",
    body: "Help residents capture their stories. Train staff. Branded family books." },
  { id: "funeral_director", name: "Funeral Directors",   price_blurb: "Revenue share partnership",
    body: "Offer The Living Book to families as part of meaningful aftercare." },
  { id: "corporate",        name: "Corporate Legacy",    price_blurb: "From £200/employee/yr",
    body: "A retirement or long-service gift that honours an entire career." },
  { id: "genealogy",        name: "Genealogy Societies", price_blurb: "Bulk licensing available",
    body: "Combine your members' research with first-person living memory." },
];

export const UPGRADE_PROMPTS = {
  voice_locked:    { headline: "Preserve your voice.",       body: "Your voice is the most precious part of this story. Subscribe to preserve it — and let future generations hear you speak." },
  photo_locked:    { headline: "Bring it to life.",           body: "Add a photograph to this memory. Subscribe to bring your story to life." },
  ai_limit:        { headline: "Let us ask one more.",        body: "Let us ask you one more question. Subscribe to unlock unlimited follow-ups — each one drawing out the detail that makes a story real." },
  book_preview:    { headline: "Your book is taking shape.",  body: "Your book is taking shape beautifully. Subscribe to preview every page — and save 20–25% when you order." },
  profile_limit:   { headline: "Every voice deserves space.", body: "Every family member deserves their own story space. Subscribe to add your whole family." },
  family_only:     { headline: "Built for the whole family.", body: "This is part of The Living Book Family — unlimited profiles, family tree, memorial archive and more." },
  default:         { headline: "Unlock The Living Book.",     body: "Voice, photos, unlimited follow-ups, your full book preview, and printed-book savings." },
};

// Curated topic prompts for the "Request a Memory" feature — grandchildren pick one.
export const FAMILY_REQUEST_TOPICS = [
  { id: "first_car",     label: "Tell me about your first car",                     text: "Tell me about your first car — how you got it, where it took you, and the first drive you remember." },
  { id: "dad_baby",      label: "What was Dad like as a baby?",                    text: "What was Dad like as a baby — his personality, the funny things he did, the way he made you feel?" },
  { id: "mum_baby",      label: "What was Mum like as a baby?",                    text: "What was Mum like as a baby — her personality, the funny things she did, the way she made you feel?" },
  { id: "wedding_day",   label: "Your wedding day",                                 text: "Tell me the story of your wedding day — from the morning of, to the moment you'll never forget." },
  { id: "meeting_grand", label: "The day you met each of your grandchildren",       text: "The day each of your grandchildren was born — what you felt, what you did, what you remember first." },
  { id: "first_home",    label: "Your first home together",                         text: "The first home you shared with someone you loved — how it looked, felt, and sounded on an ordinary evening." },
  { id: "school_friend", label: "Your best friend at school",                       text: "Who was your best friend at school? What did you get up to together? Are you still in touch?" },
  { id: "worst_job",     label: "The worst job you ever had",                       text: "The worst job you ever had — what it taught you and the funniest thing that happened there." },
  { id: "proudest",      label: "The moment you were proudest of me",               text: "A moment you were proudest of me — what was happening, and why it mattered to you." },
  { id: "advice",        label: "One piece of advice for the family",               text: "If you could leave one piece of advice for the whole family to keep — what would it be?" },
  { id: "war_years",     label: "What life was like during the war",                text: "What life was really like during the war — the small daily details the history books miss." },
  { id: "first_love",    label: "Your first love",                                  text: "Tell me about your first love — how you met, how it felt, and how it ended." },
];


// Monthly Themes — one per calendar month, broad enough for any life stage.
// Surfaces on the dashboard for paid users to keep the conversation alive between chapters.
export const MONTHLY_THEMES = [
  { month: 1,  key: "beginnings",  title: "Beginnings",             prompt: "What is one thing you began this year, or in a year long ago, that you are quietly proud of starting?" },
  { month: 2,  title: "Love, in all its forms",  key: "love",       prompt: "Tell me about a love that shaped you — a person, a place, a way of being — and what it taught you." },
  { month: 3,  title: "Journeys and homes",      key: "journeys",   prompt: "Describe a journey that changed something in you — a trip, a move, a long walk you still think about." },
  { month: 4,  title: "The world around you",    key: "world",      prompt: "What was the world like the year you turned twenty? The music, the weather, the news, the mood on the street." },
  { month: 5,  title: "Food and kitchens",       key: "food",       prompt: "A meal you'd cook if the whole family was coming home tomorrow — and the story of why that meal." },
  { month: 6,  title: "Summer light",            key: "summer",     prompt: "A summer you still remember. Where you were, who you were with, and what the light was like." },
  { month: 7,  title: "People who shaped you",   key: "people",     prompt: "Someone you rarely name — a teacher, a neighbour, a friend — who quietly built the person you are." },
  { month: 8,  title: "The work you did",        key: "work",       prompt: "A piece of work you did — paid or unpaid — that you'd want your grandchildren to understand." },
  { month: 9,  title: "School days",             key: "school",     prompt: "A single moment from a classroom, a corridor, or a playground that has stayed with you all these years." },
  { month: 10, title: "Turning points",          key: "turning",    prompt: "A decision that took you one way rather than another. What made you choose? What would you tell yourself now?" },
  { month: 11, title: "Gratitude and quiet",     key: "gratitude",  prompt: "Something small you are quietly grateful for. Nothing grand — the small mercies of an ordinary week." },
  { month: 12, title: "What you'd want kept",    key: "kept",       prompt: "If one story of yours was going to be read aloud at your grandchildren's wedding, which one would you want it to be?" },
];

// Return this month's theme (1-12)
export function currentMonthlyTheme() {
  const m = new Date().getMonth() + 1;
  return MONTHLY_THEMES.find((t) => t.month === m) || MONTHLY_THEMES[0];
}
